<template>
  <Modal ref="modal" @on-show-update="onModalShowUpdate">
    <template #header>
      <slot />
    </template>

    <Form ref="form" :rules="rules" :form-value="formValue">
      <n-form-item path="data.email" :label-props="{ for: 'email' }" :show-require-mark="false">
        <template #label>
          <FormLabel
            label="Email"
            :is-required="true"
            demo-tooltip-text='Insert "test@email.com"'
            @on-demo-click="fillExampleEmail"
          />
        </template>
        <n-input v-model:value="formValue.data.email" placeholder="email@example.com" :input-props="{ id: 'email' }" />
      </n-form-item>

      <n-form-item path="data.firstName" :label-props="{ for: 'firstName' }" :show-require-mark="false">
        <template #label>
          <FormLabel
            label="First name"
            :is-required="true"
            demo-tooltip-text='Insert "John"'
            @on-demo-click="fillExampleFirstName"
          />
        </template>
        <n-input v-model:value="formValue.data.firstName" placeholder="John" :input-props="{ id: 'firstName' }" />
      </n-form-item>

      <n-form-item path="data.lastName" :label-props="{ for: 'lastName' }" :show-require-mark="false">
        <template #label>
          <FormLabel
            label="Last name"
            :is-required="true"
            demo-tooltip-text='Insert "Smith"'
            @on-demo-click="fillExampleLastName"
          />
        </template>
        <n-input v-model:value="formValue.data.lastName" placeholder="Smith" :input-props="{ id: 'lastName' }" />
      </n-form-item>

      <n-form-item path="data.businessName" :label-props="{ for: 'businessName' }" :show-require-mark="false">
        <template #label>
          <FormLabel
            label="Business name"
            demo-tooltip-text='Insert "AlphaTheta"'
            @on-demo-click="fillExampleBusinessName"
          />
        </template>
        <n-input
          v-model:value="formValue.data.businessName"
          placeholder="Your company name"
          :input-props="{ id: 'businessName' }"
        />
      </n-form-item>
    </Form>

    <template #action>
      <n-button attr-type="submit" type="primary" @click="save()">Save</n-button>
      <n-button @click="closeModal()">Cancel</n-button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import FormLabel from './form-label.vue';
import { ref, reactive, watch } from 'vue';

import type Modal from './Modal.vue';
import type Form from './Form.vue';
import type { FormItemRule, FormRules } from 'naive-ui';
import type {
  PolicyholderFormModalOnSubmitPropsModel,
  PolicyholderFormModalPropsModel,
} from '../common/models/contact-catalogs/policyholder-form-modal-props.model';
import type { PolicyholderFormDataDto } from '../common/dtos/policyholder-list.dto';

const modal = ref<InstanceType<typeof Modal> | null>(null);
const form = ref<InstanceType<typeof Form> | null>(null);
const { data: $auth } = useAuth();

const openModal = () => {
  modal.value?.openModal();
};

const closeModal = () => {
  modal.value?.closeModal();

  resetForm();
};

defineExpose({ openModal, closeModal });

function onModalShowUpdate(value: boolean): void {
  if (!value) {
    resetForm();
  }
}

const props = defineProps<PolicyholderFormModalPropsModel>();
const propsReactive = reactive(props);

const emits = defineEmits<{
  (e: 'onSubmit', model: PolicyholderFormModalOnSubmitPropsModel): void;
}>();

const rules: FormRules = {
  data: {
    email: [
      {
        required: true,
        type: 'email',
        trigger: ['input', 'blur'],
        message: 'Email is required with valid format',
      },
      {
        required: true,
        type: 'email',
        trigger: ['input', 'blur'],
        validator(rule: FormItemRule, value: string) {
          if (value.trim() === $auth.value?.email) {
            return new Error('Policyholder email cannot be same as logged in user email');
          }
          return true;
        },
      },
    ],
    businessName: {
      required: false,
      trigger: ['input', 'blur'],
      max: 100,
      message: 'Business name cannot be longer than 100 characters',
    },
    firstName: {
      required: true,
      trigger: ['input', 'blur'],
      max: 50,
      message: 'First name is required',
    },
    lastName: {
      required: true,
      trigger: ['input', 'blur'],
      max: 50,
      message: 'Last name is required',
    },
  },
};

const formValue = ref<{ data: PolicyholderFormDataDto }>({
  data: {
    email: '',
    businessName: null,
    firstName: '',
    lastName: '',
  },
});

function fillExampleEmail() {
  formValue.value.data.email = 'test@email.com';
  form.value?.formRef?.validate();
}

function fillExampleFirstName() {
  formValue.value.data.firstName = 'John';
  form.value?.formRef?.validate();
}

function fillExampleLastName() {
  formValue.value.data.lastName = 'Smith';
  form.value?.formRef?.validate();
}

function fillExampleBusinessName() {
  formValue.value.data.businessName = 'AlphaTheta';
  form.value?.formRef?.validate();
}

function emitSave(): void {
  emits('onSubmit', formValue.value.data);

  closeModal();
}

async function save(): Promise<void> {
  await form.value?.formRef?.validate(async (errors) => {
    if (!errors) {
      emitSave();
    }
  });
}

function resetForm(): void {
  formValue.value.data.email = '';
  formValue.value.data.businessName = null;
  formValue.value.data.firstName = '';
  formValue.value.data.lastName = '';
}

watch(
  () => propsReactive.policyholder,
  () => {
    if (propsReactive.policyholder) {
      const policyholder = propsReactive.policyholder;

      formValue.value.data.email = policyholder.email;
      formValue.value.data.businessName = policyholder.businessName;
      formValue.value.data.firstName = policyholder.firstName;
      formValue.value.data.lastName = policyholder.lastName;
    }
  },
);
</script>
